// OddsConverter.js
export class OddsConverter {
    static FORMATS = {
      DECIMAL: 'Decimal',
      AMERICAN: 'American',
      FRACTIONAL: 'Fractional',
      HONG_KONG: 'Hong Kong',
      INDONESIAN: 'Indonesian',
      MALAYSIAN: 'Malaysian',
      INDO_MALAY: 'Indo/Malay',
      PROBABILITY: 'Probability',
      UK_FRACTION: 'UK Fractional',
      US_MONEYLINE: 'US Moneyline',
      ROI: 'ROI',
      EUROPEAN: 'European',
      REAL: 'Real Odds',
      DECIMAL_PERCENTAGE: 'Decimal Percentage',
      IMPLIED_PROBABILITY: 'Implied Probability',
      MALAY_ODDS: 'Malay Odds',
      THAI_ODDS: 'Thai Odds',
      VIETNAMESE_ODDS: 'Vietnamese Odds',
      CHINESE_ODDS: 'Chinese Odds',
      SINGAPOREAN_ODDS: 'Singaporean Odds',
      MARKET_MARGIN: 'Market Margin',
      FAIR_VALUE: 'Fair Value',
      EXPECTED_VALUE: 'Expected Value',
      BETTING_MARGIN: 'Betting Margin'
    };
  
    static validateOdds = (odds, format) => {
      if (!odds || odds === '') return false;
      const num = parseFloat(odds);
      switch (format) {
        case this.FORMATS.DECIMAL:
          return num > 1;
        case this.FORMATS.PROBABILITY:
          return num > 0 && num < 1;
        case this.FORMATS.HONG_KONG:
          return num > 0;
        case this.FORMATS.INDONESIAN:
        case this.FORMATS.MALAYSIAN:
        case this.FORMATS.INDO_MALAY:
          return true;
        case this.FORMATS.FRACTIONAL:
        case this.FORMATS.UK_FRACTION:
          return odds.includes('/') && odds.split('/').every(n => !isNaN(n));
        default:
          return !isNaN(num);
      }
    };
  
    static toDecimal = (odds, format) => {
      if (!this.validateOdds(odds, format)) {
        throw new Error(`Invalid odds format: ${odds} for ${format}`);
      }
  
      const converters = {
        [this.FORMATS.DECIMAL]: this.decimalToDecimal,
        [this.FORMATS.AMERICAN]: this.americanToDecimal,
        [this.FORMATS.FRACTIONAL]: this.fractionalToDecimal,
        [this.FORMATS.HONG_KONG]: this.hongkongToDecimal,
        [this.FORMATS.INDONESIAN]: this.indonesianToDecimal,
        [this.FORMATS.MALAYSIAN]: this.malaysianToDecimal,
        [this.FORMATS.INDO_MALAY]: this.indoMalayToDecimal,
        [this.FORMATS.PROBABILITY]: this.probabilityToDecimal,
        [this.FORMATS.UK_FRACTION]: this.ukFractionToDecimal,
        [this.FORMATS.US_MONEYLINE]: this.usMoneylineToDecimal,
        [this.FORMATS.ROI]: this.roiToDecimal,
        [this.FORMATS.EUROPEAN]: this.europeanToDecimal,
        [this.FORMATS.REAL]: this.realOddsToDecimal,
        [this.FORMATS.DECIMAL_PERCENTAGE]: this.decimalPercentageToDecimal,
        [this.FORMATS.IMPLIED_PROBABILITY]: this.impliedProbabilityToDecimal,
        [this.FORMATS.MALAY_ODDS]: this.malayOddsToDecimal,
        [this.FORMATS.THAI_ODDS]: this.thaiOddsToDecimal,
        [this.FORMATS.VIETNAMESE_ODDS]: this.vietnameseOddsToDecimal,
        [this.FORMATS.CHINESE_ODDS]: this.chineseOddsToDecimal,
        [this.FORMATS.SINGAPOREAN_ODDS]: this.singaporeanOddsToDecimal,
        [this.FORMATS.MARKET_MARGIN]: this.marketMarginToDecimal,
        [this.FORMATS.FAIR_VALUE]: this.fairValueToDecimal,
        [this.FORMATS.EXPECTED_VALUE]: this.expectedValueToDecimal,
        [this.FORMATS.BETTING_MARGIN]: this.bettingMarginToDecimal
      };
  
      const converter = converters[format];
      if (!converter) {
        throw new Error(`Unsupported odds format: ${format}`);
      }
  
      return converter(odds);
    };
  
    // Base conversion methods
    static decimalToDecimal = (odds) => parseFloat(odds);
    
    static americanToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds > 0 ? (odds / 100) + 1 : (100 / Math.abs(odds)) + 1;
    };
    
    static fractionalToDecimal = (odds) => {
      const [num, den] = odds.split('/').map(parseFloat);
      return (num / den) + 1;
    };
    
    static hongkongToDecimal = (odds) => parseFloat(odds) + 1;
    
    static indonesianToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds > 0 ? odds + 1 : (1 / Math.abs(odds)) + 1;
    };
    
    static malaysianToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds > 0 ? odds + 1 : (1 / Math.abs(odds)) + 1;
    };
  
    // Additional conversion methods
    static indoMalayToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds > 0 ? odds + 1 : (1 / Math.abs(odds)) + 1;
    };
  
    static probabilityToDecimal = (odds) => 1 / parseFloat(odds);
    
    static ukFractionToDecimal = this.fractionalToDecimal;
    
    static usMoneylineToDecimal = this.americanToDecimal;
    
    static roiToDecimal = (odds) => parseFloat(odds) / 100 + 1;
    
    static europeanToDecimal = (odds) => parseFloat(odds);
    
    static realOddsToDecimal = (odds) => parseFloat(odds) + 1;
  
    static decimalPercentageToDecimal = (odds) => 100 / parseFloat(odds);
    
    static impliedProbabilityToDecimal = (odds) => 1 / (parseFloat(odds) / 100);
    
    static malayOddsToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds > 0 ? odds + 1 : (1 / Math.abs(odds)) + 1;
    };
  
    static thaiOddsToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds > 0 ? odds + 1 : (1 / Math.abs(odds)) + 1;
    };
  
    static vietnameseOddsToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds > 0 ? odds + 1 : (1 / Math.abs(odds)) + 1;
    };
  
    static chineseOddsToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds + 1;
    };
  
    static singaporeanOddsToDecimal = (odds) => {
      odds = parseFloat(odds);
      return odds > 0 ? odds + 1 : (1 / Math.abs(odds)) + 1;
    };
  
    static marketMarginToDecimal = (odds) => parseFloat(odds) + 1;
    
    static fairValueToDecimal = (odds) => parseFloat(odds);
    
    static expectedValueToDecimal = (odds) => parseFloat(odds) + 1;
    
    static bettingMarginToDecimal = (odds) => 1 / (parseFloat(odds) / 100);
  }